import { useMemo } from "react";
import { ImageIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { STYLE_META } from "../../../longimage/model/blocks";
import { estimateBlockHeight } from "../../../longimage/model/estimateHeight";
import type { CanvasBlockForm } from "../../../longimage/model/canvasBridge";

/**
 * 长图模板卡面 —— 用 `schema_json` 的骨架**画出这套模板的形状**(2026-07-30 D8 收尾)。
 *
 * ═══ 为什么不是一张封面图 ═════════════════════════════════════════════════════
 * V23 seed 的 11 套模板 `cover_image_url` 是空的,而且是**有意**空着:封面是二进制,
 * 进不了 Flyway,写一个各环境都 404 的 key 比空着更糟。于是模板墙是一面「暂无封面」。
 *
 * 但再想一层:模板存的**本来就不是画面,是骨架**(内容是租户的,画面要出图才有)。
 * 贴一张成品封面反而在暗示「套了就长这样」—— 那是误导。用户在这一屏真正要判断的是
 * 「这套模板是什么形状:几屏长、先图后文还是图文交替、有没有参数表/价目/二维码」,
 * 而那**全部写在 schema_json 里**,零二进制、随模板自动更新、各环境一致。
 *
 * ★高度按 `estimateBlockHeight` 真实估算再归一化,不是每块等分 ——
 *   等分会把「一张首屏大图 + 七条窄文案」画成八条一样宽的带子,那恰好抹掉了形状本身。
 *   这份估高与搭建器「④ 多长 · 约 N 屏」同源,所以卡面上的比例和套用后看到的一致。
 */

/** schema_json.blocks 的最小形状(比 CanvasBlockForm 宽松:DB 里的陈年 JSON 什么都可能有)。 */
interface SkeletonBlock {
  primitive?: unknown;
  styleTag?: unknown;
  props?: unknown;
}

export interface TemplateSkeletonPreviewProps {
  /** `PosterTemplateBrief.schemaJson`(未验型的原始 JSON)。 */
  schemaJson: unknown;
  className?: string;
}

/** 原语 → 卡面上画成什么。data 的「网格」与 text 的「线条」要一眼能分开。 */
type Shape = "image" | "text" | "data";

function shapeOf(styleTag: string): Shape | null {
  const p = STYLE_META[styleTag]?.primitive;
  return p === "image" || p === "text" || p === "data" ? p : null;
}

/** 读出可画的块 + 每块的相对高度(权重);读不出就返回空数组(调用方回落「暂无封面」)。 */
export function skeletonBands(schemaJson: unknown): Array<{ shape: Shape; weight: number }> {
  const blocks = (schemaJson as { blocks?: unknown } | null)?.blocks;
  if (!Array.isArray(blocks) || blocks.length === 0) return [];
  const out: Array<{ shape: Shape; weight: number }> = [];
  for (const raw of blocks as SkeletonBlock[]) {
    if (!raw || typeof raw !== "object") continue;
    const styleTag = String(raw.styleTag ?? "");
    const shape = shapeOf(styleTag);
    if (!shape) continue; // 白名单外的样式直接跳过,不画一个猜出来的形状
    let weight = 1;
    try {
      // 与搭建器「约 N 屏」同一份估高 —— 卡面比例和套用后看到的一致
      weight = estimateBlockHeight({
        id: "x",
        primitive: shape,
        styleTag,
        name: "",
        props: (raw.props ?? {}) as Record<string, unknown>,
      } as CanvasBlockForm);
    } catch {
      weight = shape === "image" ? 1000 : 200; // 估高炸了不影响画卡面,给个粗档
    }
    out.push({ shape, weight: Math.max(1, weight) });
  }
  return out;
}

export function TemplateSkeletonPreview({ schemaJson, className }: TemplateSkeletonPreviewProps) {
  const bands = useMemo(() => skeletonBands(schemaJson), [schemaJson]);
  const total = bands.reduce((sum, b) => sum + b.weight, 0);
  if (!bands.length || total <= 0) return null;

  return (
    <div
      data-template-skeleton={bands.length}
      aria-hidden
      className={cn("flex h-full w-full flex-col gap-[2px] bg-muted/40 p-1.5", className)}
    >
      {bands.map((b, i) => (
        <div
          key={i}
          /* flexBasis 按真实估高占比 —— 首屏大图就该比一条 CTA 高一大截 */
          style={{ flexGrow: b.weight, flexBasis: 0 }}
          className={cn(
            "flex min-h-[3px] items-center justify-center overflow-hidden rounded-[2px]",
            b.shape === "image" && "bg-primary/25",
            b.shape === "text" && "bg-foreground/[0.07]",
            b.shape === "data" && "bg-foreground/[0.05]"
          )}
        >
          {b.shape === "image" && (
            <ImageIcon className="h-3 w-3 text-primary/50" strokeWidth={1.5} />
          )}
          {b.shape === "text" && <span className="h-[2px] w-1/2 rounded-full bg-foreground/25" />}
          {b.shape === "data" && (
            <span className="flex w-3/4 flex-col gap-[2px]">
              <span className="h-[2px] w-full rounded-full bg-foreground/20" />
              <span className="h-[2px] w-full rounded-full bg-foreground/20" />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
